import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ForumIcon from '@mui/icons-material/Forum';
import { useNavigate } from 'react-router-dom';

const getInterestStorageKey = (userId) => `ecommerce_interest_${userId || 'guest'}`;
const MAX_NOTES_LENGTH = 2000;

function EcommerceInterest({ user }) {
  const navigate = useNavigate();
  const [notes, setNotes] = useState('');
  const [interestRegistered, setInterestRegistered] = useState(false);
  const [loadingSavedInterest, setLoadingSavedInterest] = useState(true);
  const [submittingInterest, setSubmittingInterest] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const storageKey = useMemo(() => getInterestStorageKey(user?.id), [user?.id]);
  const backendUrl = process.env.REACT_APP_BACKEND_API_URL;

  const loadFromLocal = () => {
    const savedInterest = localStorage.getItem(storageKey);
    if (!savedInterest) {
      setInterestRegistered(false);
      setNotes('');
      return;
    }

    try {
      const parsed = JSON.parse(savedInterest);
      setInterestRegistered(true);
      setNotes(parsed.notes || '');
    } catch {
      setInterestRegistered(false);
      setNotes('');
    }
  };

  const saveToLocal = (notesValue = '') => {
    const interestPayload = {
      userId: user?.id || null,
      name: user?.name || '',
      email: user?.email || '',
      notes: notesValue,
      registeredAt: new Date().toISOString(),
    };
    localStorage.setItem(storageKey, JSON.stringify(interestPayload));
  };

  useEffect(() => {
    let isActive = true;

    const loadInterest = async () => {
      setLoadingSavedInterest(true);
      setFeedback(null);

      const token = localStorage.getItem('authToken');
      if (!backendUrl || !token) {
        loadFromLocal();
        if (isActive) {
          setLoadingSavedInterest(false);
        }
        return;
      }

      try {
        const response = await fetch(`${backendUrl}/api/ecommerce/interest`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.message || 'Failed to load saved interest');
        }

        if (data?.success && data?.interest) {
          const backendNotes = String(data.interest.notes || '');
          if (isActive) {
            setInterestRegistered(true);
            setNotes(backendNotes);
          }
          saveToLocal(backendNotes);
        } else if (isActive) {
          loadFromLocal();
        }
      } catch (error) {
        if (isActive) {
          loadFromLocal();
          setFeedback({
            type: 'warning',
            message: 'Could not load saved interest from server. Showing local data.',
          });
        }
      } finally {
        if (isActive) {
          setLoadingSavedInterest(false);
        }
      }
    };

    loadInterest();
    return () => {
      isActive = false;
    };
  }, [backendUrl, storageKey]);

  const handleRegisterInterest = async () => {
    const normalizedNotes = notes.trim().slice(0, MAX_NOTES_LENGTH);
    const token = localStorage.getItem('authToken');

    setSubmittingInterest(true);
    setFeedback(null);

    if (!backendUrl || !token) {
      saveToLocal(normalizedNotes);
      setInterestRegistered(true);
      setFeedback({
        type: 'warning',
        message: 'Server unavailable. Interest saved locally only.',
      });
      setSubmittingInterest(false);
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/ecommerce/interest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          notes: normalizedNotes,
          source: 'web-app',
        }),
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to save interest');
      }

      const backendNotes = String(data?.interest?.notes ?? normalizedNotes);
      setNotes(backendNotes);
      saveToLocal(backendNotes);
      setInterestRegistered(true);
      setFeedback({
        type: 'success',
        message: 'Interest registered and saved to backend database.',
      });
    } catch (error) {
      saveToLocal(normalizedNotes);
      setInterestRegistered(true);
      setFeedback({
        type: 'warning',
        message: 'Interest saved locally, but backend save failed. Please try again later.',
      });
    } finally {
      setSubmittingInterest(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 980, mx: 'auto', py: { xs: 1, md: 2 } }}>
      <Card
        sx={{
          borderRadius: 4,
          mb: 3,
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          color: 'white',
          boxShadow: '0 20px 50px rgba(15, 23, 42, 0.35)',
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 5 } }}>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
            E-Commerce Is Coming Soon
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, maxWidth: 740 }}>
            Soon you will be able to publish products with your own prices, accept online orders,
            and receive customer messages directly in this app.
          </Typography>
        </CardContent>
      </Card>

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {[
          {
            icon: <StorefrontIcon fontSize="large" />,
            title: 'Publish Your Catalog',
            description: 'Show items, images, and availability for your business online.',
          },
          {
            icon: <PriceCheckIcon fontSize="large" />,
            title: 'Set Your Price',
            description: 'Control item pricing and keep your online list updated quickly.',
          },
          {
            icon: <LocalShippingIcon fontSize="large" />,
            title: 'Get Online Orders',
            description: 'Receive and manage orders from customers in one place.',
          },
          {
            icon: <ForumIcon fontSize="large" />,
            title: 'Customer Messages',
            description: 'Let customers ask questions before ordering and respond faster.',
          },
        ].map((feature) => (
          <Grid item xs={12} sm={6} key={feature.title}>
            <Card sx={{ height: '100%', borderRadius: 3 }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                  <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                    {feature.title}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            Register Your Interest
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Share what you need first. We will prioritize features based on this feedback.
          </Typography>

          {loadingSavedInterest && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Loading your saved interest...
            </Alert>
          )}

          {interestRegistered && !feedback && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Interest already registered. You can update your notes anytime.
            </Alert>
          )}

          {feedback && (
            <Alert severity={feedback.type} sx={{ mb: 2 }}>
              {feedback.message}
            </Alert>
          )}

          <TextField
            fullWidth
            multiline
            minRows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value.slice(0, MAX_NOTES_LENGTH))}
            placeholder="Example: I want customers to place orders by item and quantity, and message on WhatsApp."
            helperText={`${notes.length}/${MAX_NOTES_LENGTH} characters`}
            sx={{ mb: 2 }}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              variant="contained"
              onClick={handleRegisterInterest}
              disabled={loadingSavedInterest || submittingInterest}
            >
              {submittingInterest
                ? 'Saving...'
                : interestRegistered
                  ? 'Update Interest'
                  : 'Register Interest'}
            </Button>
            <Button variant="outlined" onClick={() => navigate('/')}>
              Back To Dashboard
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

export default EcommerceInterest;
